/* eslint-disable max-lines -- Just a collection of interfaces, not worth splitting up */

import {
    type PaymentReportPosting,
    PaymentReportPostingAbstract,
    cookPaymentReportPosting,
} from "utils/paymentReportPosting";
import type { Address, CommonProviderSettings, PaymentProcessor } from "@vyne-shared/api/responses";
import type { BasisPoints, StrictlyBasisPoints, StrictlyDollars } from "@vyne-shared/utils/currency";
import type { PmsBalances, RequestChannel, WithBalances } from "@vyne-shared/api/VynePayments/responses";
import type { ProviderSettingsPatient, ProviderSettingsPractice } from "utils/providerSettings";
import type { TransactionsCredential } from "./requests";

/**
 * Login response provided by the Payments API.
 * See Vyne.Payments.CoreApi.Models.LoginResponse.
 */
export interface LoginResponse {
    /** Authentication token used to make Payments API requests. */
    authToken: string;

    /**
     * Trellis JWT returned to use as a refresh token, if necessary. If not provided, original login credentials should
     * be used as the refresh "token".
     */
    trellisJWT?: string;

    /** Unique ID of the Payments customer. */
    customerId: number;

    /** General name of the Payments customer. */
    customerName: string;

    /** Facility id **/
    facilityId: string;

    /** Indicates whether the Payments customer's payment processor account is configured and active. */
    isActive: boolean;

    /** Indicates whether the Payments customer has elected to sign up for Payments. */
    isSignedUp: boolean;

    /** The LaunchDarkly Secure Mode hash for the user. */
    ldHash: string;

    /** Is PMS (Vyne Sync) integration enabled for the active Provider? */
    pmsIntegrated: boolean;

    /** Provider id **/
    providerId: number;

    /** Common settings for a Provider. */
    settings: CommonProviderSettings;

    /** Point in time at which on-demand PMS sync was requested, if any. */
    syncRequestedAt?: number;

    /** Point in time (in JS ticks) at which SSO token login was first attempted during the login request, if applicable. */
    ssoTokenAttemptedAt?: number;

    /** The Trellis Global Customer ID associated with the Payments customer. Required for LaunchDarkly integration. */
    trellisGlobalCustomerId: number;

    /** Unique ID of the Trellis user, if applicable. */
    trellisUserId?: number;

    /** Unique ID of the Payments user. */
    userId: number;

    /** Unique name of the Payments user. */
    userName: string;

    /** Conveys whether or not the provider has PMS write-back support. */
    hasWriteBackSupport?: boolean;

    /** Pre-computed per-provider statistics for Pendo. */
    pendoStats?: ProviderPendoStats;

    /** Is automated writeback enabled? */
    autoWritebackEnabled: boolean;

    /** Indicates when PMS integration is enabled, and the particular PMS software in use supports Paper statement integration. */
    paperStatementCapable?: boolean;
}

/**
 * Pre-computed per-provider statistics for Pendo. Not used in the ui except for being sent to Pendo verbatim.
 * Please refer to API code for further documentation.
 */
export interface ProviderPendoStats {
    paymentCount30d: number;
    paymentAmount30d: number;
    selfSignUp: boolean;
    isPaymentMerchantAgreementComplete: boolean;
    firstPaymentRequestDate: number | null;
    firstPaymentDate: number | null;
    provisional: boolean;
}

/**
 * Response to get configuration for a Provider.
 * See Vyne.Payments.CoreApi.Models.ProviderConfigQuery.
 */
export interface ProviderConfigResponse {
    /** The payment processor the Provider is going to use. */
    paymentProcessor: PaymentProcessor;

    /** GPI credentials for the Transactions API. */
    transactionsCredential?: TransactionsCredential;

    /** Connected Account ID for Stripe payments. */
    stripeConnectedAccountId?: string;
}

/**
 * An individual Patient/Responsible Party that can be manipulated in the system.
 * See Vyne.Payments.BusinessLogic.Queries.ExistingPatientResult.
 */
export interface ExistingPatientResult extends WithBalances {
    /** Primary ID for a P/RP in the Payments system. */
    id?: number;

    /** Family (last) name of the P/RP. */
    familyName: string;

    /** Given (first) name of the P/RP. */
    givenName: string;

    /** Phone number of the P/RP. */
    phone?: string;

    /** Email address of the P/RP. */
    email?: string;

    /** Current balance of the P/RP, in basis points (cents). */
    balance?: StrictlyBasisPoints;

    /** Point in time the P/RP was last updated, in UTC, expressed in milliseconds since the Unix epoch. */
    updateDate: number;

    /** PMS (VyneSync) identifier, if linked. */
    pmsId?: string;

    /** True if we're PMS-linked, and this patient is a guarantor, rather than a dependent. */
    isGuarantor?: boolean;

    /** True if Mason integration is enabled, and patient is a Payments P/RP without a linked Mason record. */
    unlinked?: boolean;

    /** Date of Birth, if available from PMS. In MM/dd/yyyy format. */
    dateOfBirth?: string;

    /** PMS balances, if applicable. */
    pmsBalances?: PmsBalances;

    /** Means of communication for a Payment Request. */
    requestChannel: RequestChannel;

    /** Are we allowed to contact/communicate with the P/RP? */
    allowCommunication: boolean;

    /** True if P/RP Phone or Email is overriding a fact from the PMS. */
    pmsOverridden?: boolean;

    /** True in PMS-integrated scenarios where the patient would not show up for selection in a Bulk Request,
     * because the Payments P/RP has newer information than what's in the PMS. For possible future use. */
    bulkIneligible?: boolean;

    /** If defined, indicates a point in time at which a payment that wasn't yet recorded in the PMS was made.
     * This is a particular kind of bulk-ineligible scenario. */
    paidWithoutWriteBackDate?: number;

    /** True if the PMS has one or more outstanding (unpaid) insurance claims for this patient. */
    hasOutstandingClaims: boolean;
}

/**
 * Information on a single Patient/Responsible Party in the system, used to view status and activity.
 * See Vyne.Payments.BusinessLogic.Queries.PatientActivityResult.
 */
export interface PatientActivityResult {
    /** Internal ID of the patient */
    patientId: number;

    /** Full name of the P/RP. */
    patientName: string;

    /** Current balance of the P/RP, in basis points (cents). */
    balance: BasisPoints;

    /** Point in time the P/RP was last updated, in UTC, expressed in milliseconds since the Unix epoch. */
    updateDate: number;

    /** P/RP current payment status */
    paymentStatus: PaymentStatus;

    /** Does P/RP have a pending Sale (and therefore UI should show "Retry Payment")? */
    hasPendingSale: boolean;

    /** Are we allowed to contact/communicate with the P/RP? */
    allowCommunication: boolean;
}

/** Status values indicating how to display an individual P/RP's activity. */
export enum PaymentStatus {
    Paid = "Paid",
    NoAmount = "NoAmount",
    Unsent = "Unsent",
    Outstanding = "Outstanding",
}

/**
 * An individual metric from the Payments system.
 * See Vyne.Payments.BusinessLogic.Queries.Metric.
 */
export interface Metric {
    /** The title or name of the metric. */
    title: string;

    /** The value of the metric. */
    value: number | StrictlyDollars | null;

    /** Indicates if the metric value represents a dollar amount. */
    isCurrency: boolean;

    /** The percentage change of the metric value compared to the previous period, if applicable. */
    percentChange: number | null;

    /** The absolute change of the metric value compared to the previous period, if applicable. */
    valueChange: number | null;
}

/** Values indicating the type of events present in P/RP history. */
export enum EventType {
    /** A transaction that changed the P/RP's balance. */
    Transaction = "Transaction",

    /** A communication (notification) sent to the P/RP. */
    Communication = "Communication",
}

/** Types of entities that could initiate a given P/RP history event. */
export enum EventInitiatorType {
    /** A logged-in billing administrator accessing Payments via Trellis. */
    BillingAdmin = "BillingAdmin",

    /** A logged-in patient using the patient portal. */
    Patient = "Patient",

    /** A Vyne system component (another part of Payments, e.g. Vyne Sync integration or auto-resend). */
    VyneSystem = "VyneSystem",
}

/** Values indicating the refund status of an individual Transaction. */
export enum RefundStatus {
    /** Default state - refund is not possible for a Transaction. */
    NotPossible = "NotPossible",

    /** Refund is possible for a Transaction but has not been performed. */
    Possible = "Possible",

    /** Transaction has been fully refunded. */
    Refunded = "Refunded",

    /** Transaction has been partially refunded. */
    RefundedPartial = "RefundedPartial",
}

/**
 * Status values for an individual transaction event.
 * See Vyne.Payments.BusinessLogic.Queries.PaidReportDetailStatus.
 */
export type TransactionStatus = "Paid" | "RefundFull" | "RefundPartial";

/** Source values for an individual transaction event. */
export type TransactionSource =
    | "None"
    | "Dashboard"
    | "Email"
    | "SMS"
    | "Statement"
    | "API"
    | "DashboardLink"
    | "BillPay"
    | "DashboardInsurance";

interface ActiveWriteback {
    postedDate: number;
    username: string;
}

/** Method of communication sent. */
export type CommunicationChannel = "SMS" | "Email" | "Statement";

/**
 * An individual event history entry for a particular Patient/Responsible Party.
 * See Vyne.Payments.BusinessLogic.Queries.PatientHistoryResult.
 */
export interface PatientHistoryResult {
    /** The ID of the individual event. This could be a Transaction or CommunicationLog ID. */
    eventId: number;

    /** The point in time the event occurred, in UTC, expressed in milliseconds since the Unix epoch. */
    eventDate: number;

    /** The type of event. */
    type: EventType;

    /** The sub-type of communications events. */
    subType?: CommunicationType;

    /** The sub-type of transaction events. */
    txSubType?: TransactionType;

    /** The entity responsible for initiating the event. */
    initiator: EventInitiatorType;

    /** The name of the entity responsible for initiating the event, when available. */
    initiatorName?: string;

    /** The amount of the transaction activity, in basis points (cents). Only present for transaction events. */
    amount?: BasisPoints;

    /** The brand of the credit card used for the transaction. Only present for transaction events. */
    cardBrand?: string;

    /** The last four digits of the credit card used for the transaction. Only present for transaction events. */
    cardLastFour?: string;

    /** Status of the Transaction with respect to refunding. Only present for transaction events. */
    refundStatus?: RefundStatus;

    /** Channel used for communicating with the P/RP. Only present for communication events. */
    commChannel?: CommunicationChannel;

    /** Array of refunds on this transaction. */
    refunds?: PatientHistoryRefund[];

    /** Writeback date and user for the transaction. Null when not written back to PMS. */
    activeWriteback: ActiveWriteback | null;
}

/**
 * The types of notifications (communications) that can be sent to a P/RP.
 * See Vyne.Payments.Common.Models.CommunicationType
 */
export type CommunicationType = "UpdatedBalance" | "ConfirmPayment" | "ResendBalance";

/**
 * The types of transaction events that can occur.
 * See Vyne.Payments.BusinessLogic.Queries.TransactionType.
 */
export type TransactionType = "Request" | "Payment" | "AutoAdjust" | "ManAdjust";

/**
 * An entry for any refunds that have already occurred on a Transaction.
 * See Vyne.Payments.BusinessLogic.Queries.PatientHistoryRefund.
 */
export interface PatientHistoryRefund {
    /** The point in time the refund occurred, in UTC, expressed in milliseconds since the Unix epoch. */
    refundDate: number;

    /** The status of the refund (full or partial). */
    isPartial: boolean;

    /** The amount of the refund. */
    amount: StrictlyBasisPoints;

    /** The status of the refund. */
    status: RefundResultStatus;
}

/**
 * The status of a refund that has already been processed.
 * See Vyne.Payments.BusinessLogic.Queries.RefundResultStatus.
 */
export type RefundResultStatus = "Pending" | "Complete" | "Failed";

/** Identifies the results that can occur when refunding a Transaction. */
export enum RefundResult {
    /** The Transaction was not refunded. Equivalent to NULL. */
    None = "None",

    /** The Transaction was voided before the batch settled. */
    Voided = "Voided",

    /** A new return was entered. */
    Returned = "Returned",

    /** A refund is pending (Stripe only). */
    Accepted = "Accepted",
}

/** Identifies a payment or refund on the GPI or Stripe platforms. */
export interface PaymentProcessorId {
    /** "GPI" or "Stripe". */
    paymentProcessor: PaymentProcessor;

    /** Identifies the particular payment or refund, from the specified payment processor's point of view. */
    id: string;
}

/**
 * Result of refunding a transaction.
 * See Vyne.Payments.BusinessLogic.Commands.TransactionRefundResult.
 */
export interface TransactionRefundResult {
    /** True if the transaction was refunded, false otherwise. */
    success: boolean;

    /** True if the refund is pending, false otherwise. */
    pending: boolean;

    /** Detailed status indicating how the transaction was refunded, if applicable. */
    result: RefundResult;

    /** Message for the user that may provide further information, likely only in error cases. */
    message?: string;

    /** GPI or Stripe ID associated with the return transaction, if one was performed. */
    paymentProcessorId?: PaymentProcessorId;

    /** Brand of credit card that amount was refunded to. */
    cardBrand?: string;

    /** Last four digits of the credit card that amount was refunded to. */
    last4?: string;
}

/** Response from a request to merge an unlinked P/RP with a PMS patient */
export type PatientMergeResult = ExistingPatientResult;

/**
 * Conveys manual write-back capability.
 * Does not include a check for pre-existing write-backs (whether pending or completed).
 */
export type ManualWriteBackCapability = "None" | "NoneUnlinked" | "Capable";

/** Individual row of a payment report. */
export interface PaymentReportRow {
    /** Transaction ID or Refund ID */
    id: number;
    /** Unique identifier */
    key: string;
    /** Patient ID */
    patientId: number;
    /** Date of the event, in UTC, in milliseconds since the Unix epoch. */
    date: number;
    /** Full patient name */
    name: string;
    /** Patient date of birth, in MM/DD/YYYY form, if available */
    birthDate?: string;
    /** Amount, formatted in US culture */
    amount: string;
    /** Status string of transaction */
    status: TransactionStatus;
    /** Transaction source */
    source: TransactionSource;
    /** Brand of credit card that was used to pay */
    cardBrand: string;
    /** Last four digits of the credit card that was used to pay */
    last4: string;
    /** Posted type and status. */
    posting?: PaymentReportPosting;
    /** Status of the Transaction with respect to refunding. */
    refundStatus: RefundStatus;
    /** Value that changes whenever a new write-back is posted for this transaction. Not defined on refund rows.
     * This value is comparable, but should not be parsed. */
    writeBackVersion?: string;
    /** Conveys manual write-back capability.
     * Does not include a check for pre-existing write-backs (whether pending or completed). */
    manualWriteBackCapability: ManualWriteBackCapability;
    /** Rate the transaction was processed at */
    rate: string | null;
    /** Transaction fee charged for the transaction if applicable */
    transactionFee: string | null;
    /** Fee charged for the transaction, formatted in US culture */
    fee: string;
    /** Amount deposited into the patient's account, formatted in US culture */
    depositAmount: string;
    /** Charge ID of the transaction */
    chargeId: string;
}

export interface PaymentReportSummary {
    summary: {
        amountCollected: StrictlyDollars;
        amountDeposited: StrictlyDollars;
        amountFees: StrictlyDollars;
        amountRefunded: StrictlyDollars;
        endDate: string; // ISO date string
        paymentsCollected: number;
        paymentRequestsSent: number;
        startDate: string; // ISO date string
    };
    paymentRequestsSent: string;
    paymentsCollected: string;
    amountCollected: string;
    amountVoidedRefunded: string;
    amountFees: string;
    amountDeposited: string;
    key: string;
    insuranceAmountCollected: string;
    insurancePaymentsCollected: string;
    insuranceSummary: {
        amountCollected: StrictlyDollars;
        amountDeposited: StrictlyDollars;
        amountFees: StrictlyDollars;
        amountRefunded: StrictlyDollars;
        endDate: string; // ISO date string
        paymentsCollected: number;
        paymentRequestsSent: number;
        startDate: string; // ISO date string
    };
}

export interface PaymentReportProvider extends Address {
    /** Provider name */
    name: string;
}

/** Result for an entire payment report. */
export interface PaymentReportResult {
    /** Payment and Void/Refund transaction rows */
    rows: readonly Readonly<PaymentReportRow>[];

    /** Summary of the payment report. */
    summary: PaymentReportSummary;

    /** Provider address */
    provider: PaymentReportProvider;
}

/**
 * Instantiates {@link PaymentReportPosting} class instances from deserialized {@link PaymentReportRow} objects,
 * calling {@link patchupPaymentReportRow} for each raw row.
 * Freezes {@link PaymentReportResult}, so that the {@link Readonly} annotations have runtime force.
 */
export function patchupPaymentReportResult(result: PaymentReportResult) {
    if (Object.isFrozen(result)) throw new Error("patchupPaymentReportResult: input must not already be frozen");
    result.rows = Object.freeze(result.rows.map((x) => patchupPaymentReportRow(x)));
    return Object.freeze(result);
}

/** Instantiates {@link PaymentReportPosting} class instances from deserialized {@link PaymentReportRow} row objects. */
function patchupPaymentReportRow(row: PaymentReportRow) {
    if (row.posting && !(row.posting instanceof PaymentReportPostingAbstract)) {
        if (Object.isFrozen(row)) throw new Error("patchupPaymentReportRow: input must not already be frozen");
        row.posting = cookPaymentReportPosting(row.posting);
    }
    return Object.freeze(row);
}

/** Response providing current provider settings for patients. */
export interface ProviderSettingsPatientResponse extends ProviderSettingsPatient {
    /** Default system-ingrained template for text messages requesting payment. */
    paymentTextMessageDefaultTemplate: Readonly<string>;
}

/** Response providing current provider for practices. */
export interface ProviderSettingsPracticeResponse extends ProviderSettingsPractice {
    /** The URL to the provider's bill pay system. */
    billPayLink: Readonly<string>;

    // The frequency at which notifications are sent to the provider.
    notificationFrequency: Readonly<number>;
}

/**
 * Result containing the link to the Vyne Patient Portal for a single Patient.
 * See Vyne.Payments.BusinessLogic.Queries.PatientPortalLinkResult.
 */
export interface PatientPortalLinkResult {
    /** URL to the Vyne Patient Portal containing embedded access token. */
    link: string;
}

/** PMS-integration sync status, or null if no such information is available. */
export type SyncStatusQueryResult = SyncStatusQueryNonEmptyResult | null;

/** PMS-integration sync status, when such information is actually available. */
export interface SyncStatusQueryNonEmptyResult {
    /** Point in time by which sync is expected to finish. */
    dueDate: number;
    /** When did pull attempt finish? If in-progress, null. */
    finishDate: number | null;
    /** When was the most recent patient actually updated? */
    vyneSyncDate: number;
    /** The current point in time, from the API server's perspective. */
    now: number;
}

/** Aging brackets for patient balances. */
export enum BalanceAge {
    ZeroToThirty = "ZeroToThirty",
    ThirtyToSixty = "ThirtyToSixty",
    SixtyToNinety = "SixtyToNinety",
    NinetyPlus = "NinetyPlus",
}

/**
 * An individual Patient/Responsible Party in the PMS that is eligible to bulk-import to Payments.
 * See Vyne.Payments.BusinessLogic.Queries.BulkRequestQueryResult.
 */
export interface BulkRequestQueryResult extends WithBalances {
    /** Primary ID for a P/RP in the Payments system, if applicable. */
    id?: number;

    /** PMS (VyneSync) identifier. */
    pmsId: string;

    /** Family (last) name of the P/RP. */
    familyName: string;

    /** Given (first) name of the P/RP. */
    givenName: string;

    /** Date of Birth from PMS, in MM/dd/yyyy format. */
    dateOfBirth: string;

    /** Phone number of the P/RP. */
    phone?: string;

    /** Email address of the P/RP. */
    email?: string;

    /** Current balance of the P/RP, in basis points (cents), if applicable. */
    balance?: StrictlyBasisPoints;

    /** Age of the P/RP balance in the PMS. */
    balanceAge: BalanceAge;

    /** PMS balances. */
    pmsBalances: PmsBalances;

    /** Means of communication for a Payment Request. */
    requestChannel: RequestChannel;

    /** Are we allowed to contact/communicate with the P/RP? */
    allowCommunication: boolean;

    /** True if P/RP Phone or Email is overriding a fact from the PMS. */
    pmsOverridden?: boolean;

    /** Sort order provided by server that's based on balanceAge, pmsBalances.balanceAsOf, then name */
    idx: number;

    /** True if the PMS has one or more outstanding (unpaid) insurance claims for this patient. */
    hasOutstandingClaims: boolean;

    /** True if the patient is a guarantor, and any family member has an active payment plan in the PMS. */
    hasActivePaymentPlan: boolean;
}

/**
 * Response from querying for existing PMS-integrated Patient/Responsible Party records that can be used when making a
 * new bulk request.
 */
export interface BulkRequestQueryResponse {
    /** True if the system is busy with another bulk request for this same provider. */
    busy: boolean;

    /** Patient/Responsible Party records that can be used when making a new bulk request. When busy is true,
     * this will be an empty array. Always an array. */
    results: BulkRequestQueryResult[];
}

/** Summaries of the results of executing payment request batches. */
export type BulkRequestSummaries = BulkRequestSummary[];

/** Summary of the results of executing a payment request batch. */
export interface BulkRequestSummary {
    /** Identifies the batch. */
    batchId: number;

    /** Point in time at which the batch was created. */
    date: number;

    /** Total number of items in the batch. This never changes. */
    count: number;

    /** Number of items successfully processed. */
    successes: number;

    /** Number of items still in progress. */
    pendings: number;

    /** Total number of items in the batch that failed, and will not retry. */
    errors: number;

    /** True if batch item processing is still in progress. */
    inProgress: boolean;

    /** Per-patient details for items that ended up in an error state. */
    items: BulkRequestSummaryItem[];
}

/**
 * Summary of the results of executing a batch item, including patient details.
 * Useful to convey the results of a payment request batch item.
 */
export interface BulkRequestSummaryItem {
    /** Batch Item identifier. */
    id: number;

    /** State indicating the results of batch item execution. */
    state: "Pending" | "Success" | "Error";

    /** In the "Error" state, provides a message indicating what failed. */
    error?: string;

    /** Describes the most recent error encountered.
     * If populated, the item might still be re-trying. */
    lastError?: string;

    /** Identifies the PMS patient. */
    patientId: string;

    /** Full name of the patient. */
    patientName: string;
}

/** Information about a particular patient appointment. */
export interface AppointmentResult {
    /** Primary ID for a P/RP in the Payments system. */
    id?: number;

    /** PMS (VyneSync) identifier. Always present. */
    pmsId: string;

    /** The full name of the patient. */
    patientName: string;

    /** The estimated patient responsibility, in basis points (cents). */
    balance: StrictlyBasisPoints;

    /** Date of Birth from the PMS, in MM/dd/yyyy format. */
    dateOfBirth: string;

    /** Appointment Date formatted as "MM/dd/yyyy", in the provider's actual time zone. */
    appointmentDate: string;

    /** Appointment Time formatted as "hh:mm tt", in the provider's actual time zone. */
    appointmentTime: string;

    /** Uniquely identifies an appointment within a particular list of appointments.  */
    appointmentId: string;
}

/** Information about recent payment appointments. */
export type AppointmentResults = AppointmentResult[];

/**
 * Templates for a payment request for different communication methods.
 * See: Vyne.Payments.BusinessLogic.Queries.ProviderNotificationPreviewResult
 */
export interface ProviderNotificationPreviewResult {
    /** Template of an email that will be sent for a payment request. */
    emailTemplate: string;

    /** Template of an SMS message that will be sent for a payment request. */
    smsTemplate: string;
}

/**
 * Summary information for an individual PMS Patient (Guarantor or Dependent).
 * See: Vyne.Payments.BusinessLogic.Queries.PatientSummaryResult
 */
export interface PatientSummaryResult {
    /** Given (first) name of the patient. */
    givenName: string;

    /** Family (last) name of the patient. */
    familyName: string;

    /** Current requested balance of the P/RP, in basis points (cents). */
    balance: StrictlyBasisPoints;

    /** Whether this is an insurance carrier. */
    isInsuranceCarrier: boolean;
}

/**
 * Summary information for an individual PMS Patient (Guarantor or Dependent).
 * See: Vyne.Payments.BusinessLogic.Queries.PmsPatientSummaryResult
 */
export interface PMSPatientSummaryResult {
    /** Given (first) name of the patient. */
    givenName: string;

    /** Family (last) name of the patient. */
    familyName: string;

    /** Whether the patient is a guarantor (true) or dependent (false). */
    isGuarantor: boolean;

    /** Date of birth of the patient, in MM/DD/YYYY form. */
    dateOfBirth: string;

    /** Current requested balance of the P/RP, in basis points (cents), if applicable. */
    balance?: StrictlyBasisPoints;

    /** PMS balance information. */
    pmsBalances: PmsBalances;

    /** List of dependents of this patient. */
    dependents: string[];

    /**True if the PMS has one or more outstanding (unpaid) insurance claims for this patient. */
    hasOutstandingClaims: boolean;
}

/**
 * Summary information for Stripe Terminal
 */
export interface TerminalResult {
    /** Stripe Terminal ID */
    id: string;

    /** Stripe Terminal Location ID */
    locationId: string;

    /** Stripe Terminal Label */
    label: string;

    /** Terminal Last Actions with Details. */
    action: null | TerminalAction;

    /** Serial Number of Terminal */
    serialNumber: string;

    /** Current Status of Stripe Terminal */
    status: string;

    /** Device Type of Terminal */
    deviceType: string;
}

/**
 * Summary information for Stripe Terminal Action
 * https://stripe.com/docs/api/terminal/readers/object
 */
export interface TerminalAction {
    /** Failure code, only set if status is failed. */
    failureCode?: string;

    /** Detailed failure message, only set if status is failed. */
    failureMessage?: string;

    /**  Represents a reader action to process a payment intent. */
    processPaymentIntent?: TerminalActionProcessPaymentIntent;

    /**  Represents a reader action to process a setup intent. */
    processSetupIntent?: TerminalActionProcessSetupIntent;

    /**  Represents a reader action to refund a payment. */
    refundPayment?: TerminalActionRefundPayment;

    /**  Represents a reader action to set the reader display. */
    setReaderDisplay?: TerminalActionSetReaderDisplay;

    /**  Status of the action performed by the reader. One of: failed, in_progress, or succeeded */
    status: string;

    /**  Type of action performed by the reader. One of: process_payment_intent, process_setup_intent, refund_payment, or set_reader_display. */
    type: string;
}

/**
 * Summary information for Stripe Terminal Action Process Payment Intent
 * https://stripe.com/docs/api/terminal/readers/object
 */
export interface TerminalActionProcessPaymentIntent {
    paymentIntent: string;
    processConfig: null | {
        skipTipping: null | boolean;
        tipping: null | {
            amountEligible: number;
        };
    };
}

/**
 * Summary information for Stripe Terminal Action Process Setup Intent
 * https://stripe.com/docs/api/terminal/readers/object
 */
export interface TerminalActionProcessSetupIntent {
    generatedCard: null | string;
    setupIntent: string;
}

/**
 * Summary information for Stripe Terminal Action Refund Payment
 * https://stripe.com/docs/api/terminal/readers/object
 */
export interface TerminalActionRefundPayment {
    amount: null | number;
    charge: null | string;
    metadata: null | object;
    paymentIntent: null | string;
    reason: null | "duplicate" | "fraudulent" | "requested_by_customer";
    refund: null | string;
    refundApplicationFee: null | boolean;
    reverseTransfer: null | boolean;
}

/**
 * Summary information for Stripe Terminal Action Set Reader Display
 * https://stripe.com/docs/api/terminal/readers/object
 */
export interface TerminalActionSetReaderDisplay {
    cart: null | {
        currency: string;
        lineItems: {
            amount: number;
            description: string;
            quantity: number;
        }[];
        tax: number;
        total: number;
    };
    type: string;
}

export interface OnboardingResultWithLink {
    state: ConnectedAccountCallToActionWithLink;
    url: string;
}

export interface OnboardingResultWithoutLink {
    state: ConnectedAccountCallToActionWithoutLink;
    url: undefined;
}

export type OnboardingResult = OnboardingResultWithLink | OnboardingResultWithoutLink;

export type ConnectedAccountCallToActionWithLink = "ContinueEnrollment" | "FixEnrollment";

export type ConnectedAccountCallToActionWithoutLink =
    | "BeginEnrollment"
    | "Wait"
    | "WaitForProceed"
    | "Proceed"
    | "ContactSupport";

export type AllConnectedAccountCallToAction =
    | ConnectedAccountCallToActionWithLink
    | ConnectedAccountCallToActionWithoutLink;

/**
 * Result of creating a new Stripe Connected Account session.
 * See Vyne.Payments.BusinessLogic.Commands.ProviderConnectedAccountSessionCommand.
 */
export interface ConnectedAccountSessionResponse {
    /** The Stripe client secret used to access the session. */
    secret: string;
}

/**
 * Result for opening Stripe settings in a new tab.
 */
export interface StripeSettingsFormLinkResult {
    /** URL for the Stripe settings form. */
    url: string;

    /** Expiration time for the link, in milliseconds since the Unix epoch. */
    expiresAt: number;

    /** Created time for the link, in milliseconds since the Unix epoch. */
    created: number;
}

/** Result for checking the status of a Stripe Connected Account. */
export interface StripeAccountStatusResult {
    /** The current status of the Stripe Connected Account. */
    status: AllConnectedAccountCallToAction;

    /** Stripe requirements of the Stripe Connected Account.
     * Future requirements have been folded in here, and will all have a severity of "Eventually". */
    requirements: StripeRequirements | null;
}

export type StripeRequirementsErrorSeverity = "Unknown" | "Past" | "Currently" | "Eventually";

/** Stripe Connected Account requirements */
export interface StripeRequirements {
    /** Date by which the fields in currently_due must be collected to keep the account enabled,
     * or when future_requirements becomes the main requirements. */
    currentDeadline: number | null;

    /** Errors regarding fields that need to be collected at some point. */
    errors: {
        /** User friendly error message */
        reason: string;
        /** Severity (Payments API augments the raw Stripe response with this fact) */
        severity: StripeRequirementsErrorSeverity;
    }[];
}

/**
 * Fees that a Provider will pay for Stripe processing transactions.
 * See Vyne.Payments.BusinessLogic.Models.StripeFeesResult.
 */
export interface StripeRatesResult {
    // -----------------------------------------------------------------------------------------------
    // Online Fees
    // -----------------------------------------------------------------------------------------------

    /**
     * Percentage of each Stripe payment transaction allocated as a fee paid to Vyne.
     * Range: 0 to 100.
     * Applies to online transactions.
     */
    feePercent: number;

    /**
     * Fee paid to Vyne per Stripe payment transaction, in cents.
     * Applies to online transactions.
     */
    feePerTransaction: StrictlyBasisPoints;

    // -----------------------------------------------------------------------------------------------
    // Card Present Fees (in person)
    // -----------------------------------------------------------------------------------------------

    /**
     * Percentage of each Stripe payment transaction allocated as a fee paid to Vyne.
     * Range: 0 to 100.
     * Applies to card present (in person) transactions.
     */
    feePercentCardPresent: number;

    /**
     * Fee paid to Vyne per Stripe payment transaction, in cents.
     * Applies to card present (in person) transactions.
     */
    feePerTransactionCardPresent: StrictlyBasisPoints;

    // -----------------------------------------------------------------------------------------------
    // Patient Financing Fees
    // -----------------------------------------------------------------------------------------------

    /**
     * Percentage of each Stripe payment transaction allocated as a fee paid to Vyne.
     * Range: 0 to 100.
     * Applies to Affirm (buy now pay later) transactions.
     */
    feePercentAffirm: number;

    /**
     * Fee paid to Vyne per Stripe payment transaction, in cents.
     * Applies to Affirm (buy now pay later) transactions.
     */
    feePerTransactionAffirm: StrictlyBasisPoints;
}

/** The type of business entity in Opera. */
export type LBIBusinessEntityType =
    | "None"
    | "SoleProprietorship"
    | "Partnership"
    | "Llc"
    | "Cooperative"
    | "Nonprofit"
    | "Corporation";

/** The Opera verification status of a business entity. */
export type LBIVerificationStatus = "ActionRequired" | "PendingWithSmsProvider" | "Complete" | "Failed";

/** Job position of a contact for Opera legal business info. */
export type LBIJobPosition =
    | "None"
    | "Director"
    | "GeneralManager"
    | "VicePresident"
    | "CEO"
    | "CFO"
    | "GeneralCounsel"
    | "Other";

/**
 * Business entity data from Opera.
 * See Vyne.Payments.BusinessLogic.Clients.Opera.OperaBusinessEntityResult. Not all properties are mapped here.
 */
export interface LegalBusinessInfoResult extends Partial<Omit<BusinessEntityInfo, "contactInfo">> {
    verificationStatus: LBIVerificationStatus;

    // Replace contact info with partial
    contactInfo?: Partial<OperaContactInfo>;
}

/**
 * Business entity information for Opera.
 * See Vyne.Payments.BusinessLogic.Clients.Opera.BusinessEntityInfo.
 */
export interface BusinessEntityInfo {
    name: string;
    type: LBIBusinessEntityType;
    employerIdentificationNumber: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    websiteUrl: string;
    contactInfo: OperaContactInfo;
}

/**
 * Contact information for a business entity.
 * See Vyne.Payments.BusinessLogic.Clients.Opera.OperaContactInfo.
 */
export interface OperaContactInfo {
    firstName: string;
    lastName: string;
    title: string;
    email: string;
    phone: string;
    jobPosition: LBIJobPosition;
}

export interface ExistingInsuranceResult {
    /** id for an insurance company. */
    id: number;

    /** The insurance company name. */
    name: string;

    /** The date last updated. */
    updateDate: number;
}
